import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import app from "flarum/forum/app";
import FormModal from "flarum/common/components/FormModal";
import Button from "flarum/common/components/Button";
import Stream from "flarum/common/utils/Stream";
import classList from "flarum/common/utils/classList";
import { messagePreview } from "../../common/utils/preview";
import { authorName } from "../utils/bot";
/**
 * The reasons the server accepts, in the order they are offered.
 *
 * Mirrors `MessageFlag::REASONS`. A reason the server would reject must never
 * appear here — the request would fail with a validation error the reporter
 * cannot act on.
 *
 * `other` is last and is the one that requires an explanation, because the reason
 * alone says nothing to whoever picks the report up.
 */
const REASONS = ["spam", "inappropriate", "harassment", "off_topic", "other"];
/**
 * Reports a message to the moderators.
 *
 * A modal rather than a one-click action: the report lands in a queue a person has
 * to read, and a reason picked deliberately is worth more to them than a bare
 * count. It is also the last chance to reconsider, which a single button is not.
 *
 * Extends FormModal, not Modal: `Modal.wrapper()` returns a bare fragment, so a
 * `type="submit"` button inside it has no form and onsubmit never fires.
 */
export default class FlagMessageModal extends FormModal {
  constructor() {
    super(...arguments);
    _defineProperty(this, "reason", void 0);
    _defineProperty(this, "detail", void 0);
  }
  oninit(vnode) {
    super.oninit(vnode);
    this.reason = Stream("spam");
    this.detail = Stream("");
  }
  className() {
    return "ChatModal ChatFlagModal Modal--small";
  }
  title() {
    return app.translator.trans("ramon-chat.forum.flag.title");
  }
  content() {
    const message = this.attrs.message;
    return m("div", {
      className: "Modal-body"
    }, m("div", {
      className: "Form"
    }, m("div", {
      className: "ChatFlagModal-target"
    }, m("div", {
      className: "ChatFlagModal-target-author"
    }, authorName(message)), m("div", {
      className: "ChatFlagModal-target-excerpt"
    }, messagePreview(message, 200))), m("div", {
      className: "Form-group"
    }, m("label", null, app.translator.trans("ramon-chat.forum.flag.reason")), m("div", {
      className: "ChatFlagModal-reasons"
    }, REASONS.map(reason => m("button", {
      key: reason,
      type: "button",
      className: classList("ChatFlagModal-reason", {
        "ChatFlagModal-reason--active": this.reason() === reason
      }),
      disabled: this.loading,
      onclick: () => this.reason(reason)
    }, app.translator.trans("ramon-chat.forum.flag.reasons.".concat(reason)))))), m("div", {
      className: "Form-group"
    }, m("label", null, app.translator.trans(this.reason() === "other" ? "ramon-chat.forum.flag.detail_required" : "ramon-chat.forum.flag.detail")), m("textarea", {
      className: "FormControl",
      rows: 3,
      maxlength: 1000,
      bidi: this.detail,
      disabled: this.loading,
      placeholder: app.translator.trans("ramon-chat.forum.flag.detail_placeholder", {}, true)
    })), m("div", {
      className: "Form-group"
    }, m(Button, {
      className: "Button Button--primary Button--block",
      type: "submit",
      loading: this.loading,
      disabled: this.reason() === "other" && this.detail().trim() === ""
    }, app.translator.trans("ramon-chat.forum.flag.submit")))));
  }

  /**
   * Nothing awaits this method, so a rejection here would surface as an unhandled
   * promise rejection rather than as feedback. Every failure path is handled inline.
   */
  onsubmit(e) {
    e.preventDefault();
    if (this.loading) return;
    const detail = this.detail().trim();

    // Checked here as well as on the button and on the server. The button being
    // disabled is a courtesy; a form submits on Enter regardless.
    if (this.reason() === "other" && detail === "") return;
    this.loading = true;
    app.request({
      method: "POST",
      url: "".concat(app.forum.attribute("apiUrl"), "/chat-message-flags"),
      body: {
        data: {
          attributes: {
            messageId: Number(this.attrs.message.id()),
            reason: this.reason(),
            detail: detail || null
          }
        }
      }
    }).then(() => {
      // Locally, so the button reads "reported" without waiting for the next
      // fetch of the stream.
      this.attrs.message.pushAttributes({
        isFlagged: true
      });
      this.hide();
      app.alerts.show({
        type: "success"
      }, app.translator.trans("ramon-chat.forum.flag.sent"));
    }).catch(e => {
      var _e$response$errors$0$, _e$response;
      this.loading = false;
      app.alerts.show({
        type: "error"
      }, (_e$response$errors$0$ = e == null || (_e$response = e.response) == null || (_e$response = _e$response.errors) == null || (_e$response = _e$response[0]) == null ? void 0 : _e$response.detail) != null ? _e$response$errors$0$ : app.translator.trans("ramon-chat.forum.flag.failed"));
      m.redraw();
    });
  }
}
flarum.reg.add('ramon-chat', 'forum/components/FlagMessageModal', FlagMessageModal);